import { QuantityType, ThumbnailType } from '../../../../bom/constants';
import React, { useEffect, useState } from 'react';
import { SortOrder, sortBasedOnRecentBomOptionsList } from '../../utils';
import { getMultiSelectQuantityDetails, getQuantityConfig, getQuantityValidation } from '../../../../bom/selectors';
import { useCurrentEditedQuantityCardScrollIntoView, useCurrentEditedQuantityInputGetFocused } from '../../hooks';

import AlertWrapper from '../../../common/AlertWrapper';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import OptionName from '../OptionName';
import OptionQuantity from '../OptionQuantity';
import PropTypes from 'prop-types';
import RemoveButton from '../RemoveButton';
import StepNumber from '../StepNumber';
import { formatQuantity } from '../../../../utils/formatter';
import { getMinimumQuantity } from '../../../../price/selectors';
import { getRecentBomOptionsList } from '../../../product/selectors';
import styles from './QuantityGroup.scss';
import { useSelector } from 'react-redux';

const QuantityGroup = ({ feedback, options, question, resetFeedback, onRemoveOption, onSaveQuantity }) => {
  const recentBomOptionsList = useSelector(getRecentBomOptionsList);
  const multiSelectQuantityDetails = useSelector(getMultiSelectQuantityDetails);
  const qtyConfig = useSelector(getQuantityConfig);
  const qtyValidation = useSelector(getQuantityValidation);
  const miniQty = useSelector(getMinimumQuantity);

  const [quantities, setQuantities] = useState(
    Object.fromEntries(options.map((option) => [option.codeValue, option.quantity]))
  );

  useEffect(() => {
    setQuantities(Object.fromEntries(options.map((option) => [option.codeValue, option.quantity])));
  }, [options]);

  let { code, questionId, questionLabel, thumbnailType } = question;
  questionLabel = (questionLabel || '').trim();
  if (questionLabel.endsWith(':')) questionLabel = questionLabel.slice(0, questionLabel.length - 1);

  const selectedOptions = sortBasedOnRecentBomOptionsList(
    options.filter((option) => option.isSelected),
    recentBomOptionsList,
    SortOrder.Asc
  );
  const hasNoColorReferences = options.every((option) => !option.colorReference);
  const needToUseTexture = options.some((option) => option.useTexture);
  const showRemoveButton = options.filter((option) => !option.isSoldOut).length > 1;
  const allQuantitiesEntered =
    Object.values(multiSelectQuantityDetails).length > 0 &&
    Object.values(multiSelectQuantityDetails).every((qty) => qty > 0);

  const [quantityCardRef, currentEditedQuantityCardIndex] = useCurrentEditedQuantityCardScrollIntoView(selectedOptions);
  const [quantityInputRef, currentEditedQuantityInputIndex] = useCurrentEditedQuantityInputGetFocused(selectedOptions);

  const handleRemove = (option) => {
    if (!!onRemoveOption && typeof onRemoveOption === 'function') {
      onRemoveOption(option);
    }
  };

  const handleQuantityChanged = (key, qty) => {
    setQuantities({ ...quantities, [key]: qty });
    resetFeedback('quantity');
  };

  const handleSaveQuantity = (option) => {
    if (!!onSaveQuantity && typeof onSaveQuantity === 'function') {
      onSaveQuantity(option, quantities[option.codeValue]);
    }
  };

  return (
    <Form.Group className={`${styles['quantity-group']} mb-3`}>
      <div className={styles['question-label']}>
        <StepNumber step={`${code}-quantity`} />
        {`${questionLabel} & Quantity:`}
      </div>
      <div className={styles['quantity-description']}>
        {qtyConfig.type !== QuantityType.Common && (
          <span>
            {qtyConfig.type === QuantityType.Exact
              ? 'This product can only be ordered in the exact quantities shown in the price block.'
              : `This product can only be ordered in groups of ${formatQuantity(qtyConfig.minimumDivisor)}.`}
          </span>
        )}
        <span>{`Your total order must be minimum of ${formatQuantity(miniQty)}.`}</span>
      </div>
      <AlertWrapper show={!!feedback['quantity']} variant="danger">
        {feedback['quantity']}
      </AlertWrapper>
      <div className={styles['quantity-cards-holder']}>
        {selectedOptions.map((option, index) => {
          const isQuantityCardToBeScrolled = index === currentEditedQuantityCardIndex;
          const isQuantityInputToBeFocused = index === currentEditedQuantityInputIndex;
          return (
            <Card key={option.codeValue} ref={isQuantityCardToBeScrolled ? quantityCardRef : undefined}>
              <Card.Header>
                <OptionName
                  option={option}
                  questionId={questionId}
                  thumbnailType={
                    thumbnailType === ThumbnailType.LOCAL_IMAGE && hasNoColorReferences
                      ? ThumbnailType.LOCAL_IMAGE
                      : thumbnailType === ThumbnailType.SCENE7_IMAGE && (hasNoColorReferences || needToUseTexture)
                      ? ThumbnailType.SCENE7_IMAGE
                      : ThumbnailType.COLOR_SWATCH
                  }
                />
                {showRemoveButton && (
                  <RemoveButton aria-label={`Remove ${option.description}`} onClick={() => handleRemove(option)} />
                )}
              </Card.Header>
              <Card.Body>
                <OptionQuantity
                  isInvalid={
                    !!feedback['quantity'] &&
                    !qtyValidation.isValid &&
                    allQuantitiesEntered &&
                    quantities[option.codeValue] > 0
                  }
                  option={option}
                  quantity={quantities[option.codeValue]}
                  ref={isQuantityInputToBeFocused ? quantityInputRef : undefined}
                  showOptionName={false}
                  onQuantityChanged={handleQuantityChanged}
                  onSaveQuantity={handleSaveQuantity}
                />
              </Card.Body>
            </Card>
          );
        })}
      </div>
    </Form.Group>
  );
};

QuantityGroup.propTypes = {
  feedback: PropTypes.objectOf(PropTypes.string).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      codeValue: PropTypes.string.isRequired,
      colorGroupList: PropTypes.arrayOf(PropTypes.string).isRequired,
      colorReference: PropTypes.string.isRequired,
      componentId: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      estimatedShipDate: PropTypes.string.isRequired,
      futureShipDateSettingStatus: PropTypes.oneOf([0, 1, 2]).isRequired,
      helpLink: PropTypes.shape({
        helpText: PropTypes.string.isRequired,
        linkText: PropTypes.string.isRequired,
      }),
      inventoryMessage: PropTypes.string.isRequired,
      isFutureShipDateSettable: PropTypes.bool.isRequired,
      isOutOfStock: PropTypes.bool.isRequired,
      isSelected: PropTypes.bool.isRequired,
      isSoldOut: PropTypes.bool.isRequired,
      itemId: PropTypes.string.isRequired,
      optionText: PropTypes.string.isRequired,
      outOfStockMessage: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      sequence: PropTypes.number.isRequired,
      specialBomCharge: PropTypes.string.isRequired,
      useTexture: PropTypes.bool.isRequired,
      yearOptionPricing: PropTypes.oneOf([0, 1, 2]).isRequired,
    })
  ).isRequired,
  question: PropTypes.shape({
    code: PropTypes.string.isRequired,
    isRealParentChildQuestion: PropTypes.bool.isRequired,
    itemId: PropTypes.string.isRequired,
    mandatoryFlag: PropTypes.string.isRequired,
    needToReloadUniqueOptions: PropTypes.bool.isRequired,
    questionId: PropTypes.number.isRequired,
    questionLabel: PropTypes.string.isRequired,
    questionText: PropTypes.string.isRequired,
    questionType: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
    representationType: PropTypes.oneOf([0, 1]).isRequired,
    sampleQuestionText: PropTypes.string,
    selectionType: PropTypes.oneOf([0, 1]).isRequired,
    sequence: PropTypes.number.isRequired,
    thumbnailType: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
  }).isRequired,
  resetFeedback: PropTypes.func.isRequired,
  onRemoveOption: PropTypes.func,
  onSaveQuantity: PropTypes.func.isRequired,
};

export default QuantityGroup;
